import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { getUnixTime, parseISO } from 'date-fns';
import { get as dig, clamp, groupBy } from 'lodash';
import { defineMessages, useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { LD_PROJECT_TRASH_CAN_FLAG } from 'constants/featureFlags';
import Common from 'helpers/intl-common';
import { preloadImage } from 'helpers/asset-utils';
import { photoUriFromVariants } from 'helpers/graphql-utils';
import ItemMetaContainer from './ItemMetaContainer';
import PhotoEditorContainer from 'components/photo_editor/PhotoEditorContainer';
import VideoPlayer from 'components/videos/VideoPlayer';
import ZoomablePhoto from './ZoomablePhoto';
import useMenuActionDownload from 'components/assetFeed/actions/useMenuActionDownload';
import useMenuActionDelete from 'javascripts/components/assetFeed/actions/useMenuActionDelete';
import { useCurrentCompany, usePhotoOptimisticEdits } from 'javascripts/hooks';
import { ButtonIcon } from '@companycam/slab-web';

const KEY_LEFT = 37;
const KEY_RIGHT = 39;
const KEY_ESC = 27;

const messages = defineMessages({
  editPhoto: {
    id: 'Assets.galleryContainer.editPhoto',
    defaultMessage: 'Edit Photo',
    description: 'Action button caption',
  },
  toggleFullscreen: {
    id: 'Assets.galleryContainer.toggleFullscreen',
    defaultMessage: 'Toggle Fullscreen',
    description: 'Action button caption',
  },
  zoomOut: {
    id: 'Assets.galleryContainer.zoomOut',
    defaultMessage: 'Zoom Out',
    description: 'Action button caption',
  },
  zoomIn: {
    id: 'Assets.galleryContainer.zoomIn',
    defaultMessage: 'Zoom In',
    description: 'Action button caption',
  },
});

const NOOP = () => {};

const GalleryContainer = ({
  initialAsset,
  assets,
  onRequestClose,
  isOpen,
  onEndReached = NOOP,
  onEndReachedThreshold = 1,
  isMetadataVisible,
  isActionBarVisible,
}) => {
  const initialAssetId = initialAsset ? initialAsset.id : null;
  const intl = useIntl();
  const flags = useFlags();
  const isTrashCanEnabled = flags[LD_PROJECT_TRASH_CAN_FLAG];
  const [currentAssetId, setCurrentAssetId] = useState(initialAssetId);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [keyboardNavigationEnabled, setKeyboardNavigationEnabled] =
    useState(true);
  const { getOptimisticUri } = usePhotoOptimisticEdits();

  const linkedAssetList = useMemo(() => {
    return assets.reduce((memo, asset, idx) => {
      const prevId = idx > 0 ? assets[idx - 1].id : null;
      const nextId = idx + 1 < assets.length ? assets[idx + 1].id : null;

      return {
        ...memo,
        [asset.id]: {
          asset,
          prevId,
          nextId,
        },
      };
    }, {});
  }, [assets]);

  const currentAssetMap = linkedAssetList[currentAssetId] || {};

  const currentAsset = currentAssetMap.asset;
  const { prevId, nextId } = currentAssetMap;
  const hasPrevious = prevId !== null;
  const hasNext = nextId !== null;

  const { currentCompany } = useCurrentCompany();
  const assetCompanyId = dig(currentAsset, 'contributor.companyId', null);
  const assetFromDifferentCompany = assetCompanyId
    ? currentCompany.node_id !== assetCompanyId
    : false;

  const actionAssets = [currentAsset].filter((x) => !!x);
  const actionAssetsByTypename = groupBy(actionAssets, '__typename');
  const [actionDownload, renderModalDownload] = useMenuActionDownload({
    assets: actionAssets,
    assetsByType: {
      videos: actionAssetsByTypename['Video'] || [],
      photos: actionAssetsByTypename['Photo'] || [],
    },
  });

  const zoomIn = () => {
    const zoom = clamp(zoomLevel + 0.5, 4);
    setZoomLevel(zoom);
  };

  const zoomOut = () => {
    const zoom = clamp(zoomLevel - 0.5, 1, 4);
    setZoomLevel(zoom);
  };

  const movePrevious = useCallback(() => {
    if (hasPrevious) {
      setZoomLevel(1);
      setCurrentAssetId(prevId);
    }
  }, [hasPrevious, prevId]);

  const moveNext = useCallback(() => {
    if (hasNext) {
      setZoomLevel(1);
      setCurrentAssetId(nextId);
    }
  }, [hasNext, nextId]);

  const handleClose = useCallback(() => {
    setCurrentAssetId(null);
    setZoomLevel(1);
    onRequestClose();
  }, [onRequestClose]);

  const [actionDelete] = useMenuActionDelete({
    assets: [currentAsset],
    onCalled: () => {
      if (hasNext) {
        moveNext();
      } else if (hasPrevious) {
        movePrevious();
      } else {
        handleClose();
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setCurrentAssetId(initialAssetId);
    }
  }, [isOpen, initialAssetId]);

  useEffect(() => {
    const handleKeyboardEvent =
      isEditing || !keyboardNavigationEnabled
        ? () => {}
        : (event) => {
            switch (event.which) {
              case KEY_ESC:
                return onRequestClose();
              case KEY_LEFT:
                return movePrevious();
              case KEY_RIGHT:
                return moveNext();
              default:
            }

            return null;
          };

    document.addEventListener('keydown', handleKeyboardEvent);

    return () => {
      document.removeEventListener('keydown', handleKeyboardEvent);
    };
  }, [
    moveNext,
    movePrevious,
    onRequestClose,
    isEditing,
    keyboardNavigationEnabled,
  ]);

  useEffect(() => {
    const loadAsset = (asset) => {
      if (asset.__typename === 'Photo' && asset.assetPreviewLarge) {
        preloadImage(asset.assetPreviewLarge);
      }
    };

    if (hasPrevious && linkedAssetList[prevId]) {
      loadAsset(linkedAssetList[prevId].asset);
    }

    if (hasNext && linkedAssetList[nextId]) {
      loadAsset(linkedAssetList[nextId].asset);
    }
  }, [hasPrevious, hasNext, linkedAssetList, nextId, prevId]);

  useEffect(() => {
    const handleEndReached = () => {
      const assetsLeft = assets.length - 1 - assets.indexOf(currentAsset);
      const hasReachedThreshold = assetsLeft <= onEndReachedThreshold;
      if (hasReachedThreshold) {
        onEndReached();
      }
    };

    handleEndReached();
  }, [assets, currentAsset, onEndReached, onEndReachedThreshold]);

  if (!currentAsset) {
    return null;
  }

  const photoSource =
    getOptimisticUri(currentAsset.databaseId) || currentAsset.assetPreviewLarge;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick
      className="asset-viewer-modal"
      overlayClassName="modal"
      contentLabel="Asset Modal"
    >
      <div className="asset-gallery-content">
        <div className="asset-content-wrap">
          {currentAsset.__typename === 'Photo' && (
            <ZoomablePhoto
              isFullscreen={isFullscreen}
              source={photoSource}
              zoom={zoomLevel}
            />
          )}
          {currentAsset.__typename === 'Video' && (
            <VideoPlayer
              src={currentAsset.playbackUrl}
              posterSrc={currentAsset.assetPreviewLarge}
            />
          )}
          {hasPrevious ? (
            <ButtonIcon
              data-testid="gallery__previous-arrow"
              size="large"
              iconName="chevron-left"
              onClick={movePrevious}
              className="back-arrow"
              style={{
                position: 'absolute',
                top: 'calc(50% - 24px)',
                left: '16px',
              }}
            />
          ) : null}
          {hasNext ? (
            <ButtonIcon
              data-testid="gallery__next-arrow"
              size="large"
              iconName="chevron-right"
              onClick={moveNext}
              className="next-arrow"
              style={{
                position: 'absolute',
                top: 'calc(50% - 24px)',
                right: '16px',
              }}
            />
          ) : null}
          {isActionBarVisible ? (
            <div className="asset-toolbar">
              {currentAsset.__typename === 'Photo' && (
                <ButtonIcon
                  color="secondary"
                  shape="square"
                  iconName="gesture"
                  className="hint--bottom"
                  design="minimal"
                  aria-label={intl.formatMessage(messages.editPhoto)}
                  accessibilityLabel={intl.formatMessage(messages.editPhoto)}
                  onClick={() => {
                    setIsEditing(true);
                  }}
                />
              )}
              <ButtonIcon
                color="secondary"
                shape="square"
                iconName={isFullscreen ? 'fullscreen-exit' : 'fullscreen'}
                className="hint--bottom"
                design="minimal"
                aria-label={intl.formatMessage(messages.toggleFullscreen)}
                accessibilityLabel={intl.formatMessage(
                  messages.toggleFullscreen,
                )}
                onClick={() => {
                  setIsFullscreen(!isFullscreen);
                }}
              />
              {currentAsset.__typename === 'Photo' && (
                <ButtonIcon
                  color="secondary"
                  shape="square"
                  iconName="magnify-minus"
                  className="hint--bottom"
                  design="minimal"
                  aria-label={intl.formatMessage(messages.zoomOut)}
                  accessibilityLabel={intl.formatMessage(messages.zoomOut)}
                  onClick={zoomOut}
                />
              )}
              {currentAsset.__typename === 'Photo' && (
                <ButtonIcon
                  color="secondary"
                  shape="square"
                  iconName="magnify-plus"
                  className="hint--bottom"
                  design="minimal"
                  aria-label={intl.formatMessage(messages.zoomIn)}
                  accessibilityLabel={intl.formatMessage(messages.zoomIn)}
                  onClick={zoomIn}
                />
              )}
              {!!actionDownload && (
                <ButtonIcon
                  color="secondary"
                  shape="square"
                  iconName="cloud-download"
                  className="hint--bottom"
                  design="minimal"
                  aria-label={intl.formatMessage(Common.download)}
                  accessibilityLabel={intl.formatMessage(Common.download)}
                  onClick={actionDownload}
                />
              )}
              {actionDelete ? (
                <ButtonIcon
                  color="secondary"
                  shape="square"
                  iconName="delete"
                  className="hint--bottom"
                  design="minimal"
                  aria-label={
                    isTrashCanEnabled
                      ? intl.formatMessage(Common.moveToTrash)
                      : intl.formatMessage(Common.delete)
                  }
                  accessibilityLabel={
                    isTrashCanEnabled
                      ? intl.formatMessage(Common.moveToTrash)
                      : intl.formatMessage(Common.delete)
                  }
                  onClick={actionDelete}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        {isMetadataVisible ? (
          <ItemMetaContainer
            isHidden={isFullscreen}
            asset={currentAsset}
            onRequestClose={handleClose}
            displayCompanyName={assetFromDifferentCompany}
            setKeyboardNavigationEnabled={setKeyboardNavigationEnabled}
          />
        ) : (
          <div className="modal-close-button" onClick={onRequestClose}>
            <i className="mdi mdi-close" />
          </div>
        )}
        {isEditing ? (
          <PhotoEditorContainer
            photoId={currentAsset.databaseId}
            imageUri={photoUriFromVariants(
              currentAsset.originalUris,
              'ORIGINAL',
            )}
            imageTimeUnixS={getUnixTime(parseISO(currentAsset.capturedAt))}
            onClose={() => {
              setIsEditing(false);
            }}
          />
        ) : null}
        {renderModalDownload()}
      </div>
    </Modal>
  );
};

const MaybeAssetType = PropTypes.shape({
  __typename: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  databaseId: PropTypes.string,
  assetPreviewLarge: PropTypes.string.isRequired,
  capturedAt: PropTypes.string.isRequired,
  createdAt: PropTypes.string,
  contributor: PropTypes.shape({
    webURL: PropTypes.string,
    name: PropTypes.string,
    avatar: PropTypes.string,
    companyId: PropTypes.string,
    companyName: PropTypes.string,
  }),
  projectInfo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    databaseId: PropTypes.string.isRequired,
    formattedLine1: PropTypes.string.isRequired,
    formattedLine2: PropTypes.string.isRequired,
  }),
  originalUris: PropTypes.arrayOf(
    PropTypes.shape({
      __typename: PropTypes.string.isRequired,
      variant: PropTypes.string.isRequired,
      uri: PropTypes.string.isRequired,
    }),
  ).isRequired,
});

GalleryContainer.propTypes = {
  initialAsset: MaybeAssetType,
  assets: PropTypes.arrayOf(MaybeAssetType).isRequired,
  onRequestClose: PropTypes.func,
  isOpen: PropTypes.bool,
  onEndReached: PropTypes.func,
  onEndReachedThreshold: PropTypes.number,
  isMetadataVisible: PropTypes.bool,
  isActionBarVisible: PropTypes.bool,
};

GalleryContainer.defaultProps = {
  initialAsset: null,
  isOpen: false,
  isMetadataVisible: true,
  isActionBarVisible: true,
};
export default GalleryContainer;
